import { api } from './api';

/**
 * User Authentication Service
 * Handles all regular user authentication-related API calls and token management
 */
const userAuthService = {
  /**
   * Login with user email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Promise with user data
   */
  login: async (email, password) => {
    try {
      console.log('User login payload:', { email, password: password ? '********' : null });
      
      const response = await api.post('/api/auth/login', { email, password });
      console.log('User login response:', response.data);
      
      // Handle different possible response formats
      let token, user;
      
      // Format 1: response.data.data with token and user
      if (response?.data?.data?.token && response?.data?.data?.user) {
        ({ token, user } = response.data.data);
      } 
      // Format 2: response.data with token and user directly
      else if (response?.data?.token && response?.data?.user) {
        ({ token, user } = response.data);
      }
      
      if (token && user) {
        // Store user token and data
        localStorage.setItem('userAuthToken', token);
        localStorage.setItem('currentUser', JSON.stringify(user));
        return { success: true, user, token };
      }
      
      // If we get here, the response format was unexpected
      console.error('Unexpected login response format:', response.data);
      const error = new Error('Invalid response format from server');
      error.response = response;
      throw error;
      
    } catch (error) {
      console.error('User login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Extract error message from response or use default
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         'Login failed. Please check your credentials and try again.';
      
      const authError = new Error(errorMessage);
      authError.status = error.response?.status || 500;
      throw authError;
    }
  },

  /**
   * Logout the current user
   */
  logout: async () => {
    try {
      // Clear local storage first for immediate UI update
      localStorage.removeItem('userAuthToken');
      localStorage.removeItem('currentUser');
      
      // Call the logout endpoint to invalidate the token on the server
      try {
        await api.post('/api/auth/logout');
      } catch (error) {
        // Even if the API call fails, we still want to clear the local state
        console.warn('Logout API call failed, but clearing local state anyway:', error);
      }
      
      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('Error during logout. Please try again.');
    }
  },

  /**
   * Get the current authenticated user
   * @returns {Promise} - Promise with current user data
   */
  getCurrentUser: async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('userAuthToken');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Make API call to get current user data
      const response = await api.get('/api/auth/me');
      
      // Validate response format
      if (response.data && response.data.success && response.data.data) {
        const user = response.data.data;
        // Update stored user data if it exists
        if (user) {
          localStorage.setItem('currentUser', JSON.stringify(user));
        }
        return user;
      }
      
      throw new Error('Invalid response format from server');
      
    } catch (error) {
      // If there's an error, clear the stored token and user data
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        localStorage.removeItem('userAuthToken');
        localStorage.removeItem('currentUser');
      }
      
      console.error('Get current user error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      throw error;
    }
  },

  /**
   * Update current user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} - Promise with success message
   */
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/api/auth/update-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Update user password error:', error);
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} - True if authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('userAuthToken');
  },

  /**
   * Get current user from localStorage
   * @returns {Object|null} - User object or null
   */
  getUser: () => {
    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Check if current user is an admin (always false for regular users)
   * @returns {boolean} - False for user auth service
   */
  isAdmin: () => {
    return false;
  },

  /**
   * Get user authentication token
   * @returns {string|null} - User token or null
   */
  getToken: () => {
    return localStorage.getItem('userAuthToken');
  }
};

export default userAuthService;
